import { isCastCategory, isExtrasCategory } from './breakdownIdentity';

export type BreakdownCategoryIconToken =
    | 'users'
    | 'tag'
    | 'location'
    | 'costume'
    | 'vehicle'
    | 'makeup'
    | 'stunt'
    | 'animal'
    | 'sound'
    | 'vfx';

export type BreakdownCategoryPresentationKey =
    | 'cast'
    | 'extra'
    | 'prop'
    | 'location'
    | 'costume'
    | 'vehicle'
    | 'makeup'
    | 'stunt'
    | 'animal'
    | 'sound'
    | 'vfx'
    | 'set'
    | 'equipment';

export interface BreakdownCategoryPresentation {
    key: BreakdownCategoryPresentationKey;
    sidebarColor: string;
    reportColor: string;
    icon: BreakdownCategoryIconToken;
}

/** User-selectable colors remain ordered exactly as in the Breakdown sidebar. */
export const BREAKDOWN_CATEGORY_COLOR_PALETTE = Object.freeze([
    '#A78BFA', '#F472B6', '#FB923C', '#FBBF24', '#34D399',
    '#22D3EE', '#60A5FA', '#EF4444', '#9CA3AF', '#818CF8',
] as const);

export const BREAKDOWN_CATEGORY_PRESENTATIONS = Object.freeze([
    { key: 'cast', sidebarColor: '#A78BFA', reportColor: '#A78BFA', icon: 'users' },
    { key: 'extra', sidebarColor: '#34D399', reportColor: '#34D399', icon: 'users' },
    { key: 'prop', sidebarColor: '#FB923C', reportColor: '#FB923C', icon: 'tag' },
    { key: 'location', sidebarColor: '#22D3EE', reportColor: '#22D3EE', icon: 'location' },
    { key: 'costume', sidebarColor: '#FBBF24', reportColor: '#FBBF24', icon: 'costume' },
    { key: 'vehicle', sidebarColor: '#60A5FA', reportColor: '#60A5FA', icon: 'vehicle' },
    { key: 'makeup', sidebarColor: '#F472B6', reportColor: '#F472B6', icon: 'makeup' },
    { key: 'stunt', sidebarColor: '#F87171', reportColor: '#EF4444', icon: 'stunt' },
    { key: 'animal', sidebarColor: '#D6A56F', reportColor: '#D97706', icon: 'animal' },
    { key: 'sound', sidebarColor: '#4ADE80', reportColor: '#4ADE80', icon: 'sound' },
    { key: 'vfx', sidebarColor: '#FB7185', reportColor: '#F87171', icon: 'vfx' },
    { key: 'set', sidebarColor: '#9CA3AF', reportColor: '#9CA3AF', icon: 'tag' },
    { key: 'equipment', sidebarColor: '#A1A1AA', reportColor: '#6B7280', icon: 'tag' },
] as const satisfies readonly BreakdownCategoryPresentation[]);

const PRESENTATION_BY_KEY = new Map<BreakdownCategoryPresentationKey, BreakdownCategoryPresentation>(
    BREAKDOWN_CATEGORY_PRESENTATIONS.map(presentation => [presentation.key, presentation]),
);

const UNKNOWN_SIDEBAR_PRESENTATION: Readonly<BreakdownCategoryPresentation> = Object.freeze({
    key: 'equipment',
    sidebarColor: '#A1A1AA',
    reportColor: '#6B7280',
    icon: 'tag',
});

const presentationFor = (key: BreakdownCategoryPresentationKey): BreakdownCategoryPresentation =>
    PRESENTATION_BY_KEY.get(key)!;

/**
 * Match the Breakdown sidebar's historical category precedence exactly.
 * Persisted category colors still override this default in the component.
 */
export const getBreakdownSidebarCategoryPresentation = (
    categoryName: string,
): Readonly<BreakdownCategoryPresentation> => {
    const name = categoryName.toLowerCase();
    if (isCastCategory(categoryName)) return presentationFor('cast');
    if (isExtrasCategory(categoryName)) return presentationFor('extra');
    if (name.includes('prop')) return presentationFor('prop');
    if (name.includes('location')) return presentationFor('location');
    if (name.includes('sound')) return presentationFor('sound');
    if (name.includes('costume')) return presentationFor('costume');
    if (name.includes('vehicle')) return presentationFor('vehicle');
    if (name.includes('set')) return presentationFor('set');
    if (name.includes('makeup')) return presentationFor('makeup');
    if (name.includes('stunt')) return presentationFor('stunt');
    if (name.includes('animal')) return presentationFor('animal');
    if (name.includes('vfx')) return presentationFor('vfx');
    if (name.includes('equipment')) return presentationFor('equipment');
    return UNKNOWN_SIDEBAR_PRESENTATION;
};

/** Match the Reports screen's historical keyword precedence and fallback. */
export const getBreakdownReportCategoryPresentation = (
    categoryName: string,
): Readonly<BreakdownCategoryPresentation> => {
    const name = categoryName.toLowerCase();
    const matchOrder: readonly BreakdownCategoryPresentationKey[] = [
        'cast', 'extra', 'prop', 'location', 'costume', 'vehicle', 'makeup',
        'stunt', 'animal', 'sound', 'vfx', 'set', 'equipment',
    ];
    const key = matchOrder.find(candidate => name.includes(candidate));
    return presentationFor(key || 'set');
};
